import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const AcademicYearContext = createContext();

export const useAcademicYear = () => {
  const context = useContext(AcademicYearContext);
  if (!context) {
    throw new Error('useAcademicYear must be used within an AcademicYearProvider');
  }
  return context;
};

export const AcademicYearProvider = ({ children }) => {
  const [years, setYears] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load academic years
  const loadYears = async () => {
    // Check if user is authenticated by checking token
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/annees-scolaires');
      const { data, current } = response.data;
      
      setYears(data);
      setCurrentYear(current);
      
      // Set selected year from localStorage or use current
      const savedYearId = localStorage.getItem('selectedYearId');
      if (savedYearId) {
        const savedYear = data.find(y => y.id === parseInt(savedYearId));
        setSelectedYear(savedYear || current);
      } else {
        setSelectedYear(current);
      }
    } catch (error) {
      // Only show error if it's not an auth error
      if (error.response?.status !== 401) {
        toast.error('Erreur lors du chargement des années scolaires');
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadYears();
    
    // Listen for auth changes
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token added, reload years
          loadYears();
        } else {
          // Token removed, clear data
          setYears([]);
          setCurrentYear(null);
          setSelectedYear(null);
          setLoading(false);
        }
      }
    };
    
    // Listen for login event
    const handleUserLogin = () => {
      loadYears();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedIn', handleUserLogin);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleUserLogin);
    };
  }, []);

  // Change selected year
  const changeYear = (yearId) => {
    const year = years.find(y => y.id === yearId);
    if (year) {
      setSelectedYear(year);
      localStorage.setItem('selectedYearId', yearId.toString());
      
      // Reload data in components by triggering a custom event
      window.dispatchEvent(new CustomEvent('academicYearChanged', { detail: year }));
      
      toast.info(`Année scolaire changée : ${year.annee_scolaire}`);
    }
  };

  // Create new academic year
  const createYear = async (yearData) => {
    try {
      const response = await api.post('/annees-scolaires', yearData);
      if (response.data.success) {
        await loadYears();
        toast.success('Année scolaire créée avec succès');
        return { success: true };
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la création');
      return { success: false, error: error.response?.data?.message };
    }
  };

  // Activate academic year
  const activateYear = async (yearId) => {
    try {
      const response = await api.put(`/annees-scolaires/${yearId}/activate`);
      if (response.data.success) {
        await loadYears();
        toast.success('Année scolaire activée avec succès');
        return { success: true };
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'activation');
      return { success: false, error: error.response?.data?.message };
    }
  };

  const value = {
    years,
    currentYear,
    selectedYear,
    loading,
    changeYear,
    createYear,
    activateYear,
    refreshYears: loadYears,
  };

  return (
    <AcademicYearContext.Provider value={value}>
      {children}
    </AcademicYearContext.Provider>
  );
};